import { useEffect, useState } from 'react';
import Footer from '../home/components/Footer';

export default function TranscriptAudio10Page() {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [showFullTranscript, setShowFullTranscript] = useState(false);

  useEffect(() => {
    fetch('/transcripts/audio-10-constitutional-ai-design-defect.md')
      .then(response => response.text())
      .then(text => {
        setContent(text);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading transcript:', error);
        setLoading(false);
      });
  }, []);

  const handleDownload = () => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'audio-10-constitutional-ai-design-defect.md';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const parseMarkdown = (markdown: string) => {
    const lines = markdown.split('\n');
    const sections: { title: string; content: string[] }[] = [];
    let currentSection: { title: string; content: string[] } | null = null;

    lines.forEach(line => {
      if (line.startsWith('## ')) {
        if (currentSection) {
          sections.push(currentSection);
        }
        currentSection = {
          title: line.replace('## ', ''),
          content: []
        };
      } else if (currentSection && line.trim()) {
        currentSection.content.push(line);
      }
    });

    if (currentSection) {
      sections.push(currentSection);
    }

    return sections;
  };

  const sections = content ? parseMarkdown(content) : [];

  return (
    <div className="min-h-screen bg-white">
      
      <div className="container mx-auto px-6 lg:px-12 py-20 max-w-5xl">
        {/* Back Button */}
        <a
          href="/transcripts"
          className="inline-flex items-center gap-2 text-red-800 hover:text-red-900 font-semibold mb-8 cursor-pointer transition-colors duration-300"
        >
          <i className="ri-arrow-left-line"></i>
          Back to All Transcripts
        </a>

        {/* Header */}
        <header className="mb-12 pb-8 border-b border-slate-200">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-4 py-1.5 bg-red-100 text-red-800 rounded-full text-xs font-bold uppercase tracking-wide">
              <i className="ri-mic-line mr-1"></i>
              Audio 2
            </span>
            <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">
              ⭐ Featured Transcript
            </span>
            <span className="text-sm text-slate-500">~37 minutes</span>
            <span className="text-sm text-slate-500">•</span>
            <span className="text-sm text-slate-500">December 20, 2024</span>
          </div>
          
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
            Existing Law Forces AI Transparency and Design
          </h1>
          
          <p className="text-xl text-slate-600 mb-6">
            A comprehensive discussion exploring the legal framework for AI accountability, constitutional AI architecture, and the shift in standard of care for high-stakes AI systems.
          </p>

          {/* Topics */}
          <div className="flex flex-wrap gap-2">
            {['Constitutional AI', 'Design Defect Liability', 'Feasible Alternative Design', 'Regulatory Framework', 'Product Liability Law'].map((topic, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm"
              >
                {topic}
              </span>
            ))}
          </div>
        </header>

        {/* View Full Transcript Button - Prominent CTA */}
        {!showFullTranscript && (
          <div className="mb-12 text-center">
            <button
              onClick={() => setShowFullTranscript(true)}
              className="inline-flex items-center justify-center gap-3 px-12 py-5 bg-red-800 hover:bg-red-900 text-white text-lg font-bold transition-all duration-300 shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer hover:scale-105 rounded-xl"
            >
              <i className="ri-file-text-line text-2xl"></i>
              VIEW FULL TRANSCRIPT
              <i className="ri-arrow-down-line text-2xl"></i>
            </button>
            <p className="text-sm text-slate-500 mt-4">
              Click to read the complete transcript below
            </p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-red-800"></div>
            <p className="mt-4 text-slate-600">Loading transcript...</p>
          </div>
        )}

        {/* Transcript Content - Only show when button is clicked */}
        {!loading && content && showFullTranscript && (
          <div className="prose prose-lg max-w-none">
            {sections.map((section, index) => (
              <section key={index} className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-3 border-b border-slate-200">
                  {section.title}
                </h2>
                <div className="space-y-4">
                  {section.content.map((paragraph, pIndex) => {
                    // Check if it's a speaker line with timestamp
                    if (paragraph.match(/^\*\*\[Speaker \d+\] \[\d+\.\d+s\]\*\*/)) {
                      const parts = paragraph.match(/^\*\*\[(Speaker \d+)\] \[(\d+\.\d+)s\]\*\* (.+)$/);
                      if (parts) {
                        const [, speaker, timestamp, text] = parts;
                        const timeInSeconds = parseFloat(timestamp);
                        const minutes = Math.floor(timeInSeconds / 60);
                        const seconds = Math.floor(timeInSeconds % 60);
                        
                        return (
                          <div key={pIndex} className="flex gap-4 py-3 hover:bg-slate-50 transition-colors duration-200 rounded-lg px-4 -mx-4">
                            <div className="flex-shrink-0 w-32">
                              <div className="text-sm font-semibold text-red-800">{speaker}</div>
                              <div className="text-xs text-slate-500">{minutes}:{seconds.toString().padStart(2, '0')}</div>
                            </div>
                            <div className="flex-1 text-slate-700 leading-relaxed">
                              {text}
                            </div>
                          </div>
                        );
                      }
                    }
                    
                    // Regular paragraph or list item
                    if (paragraph.startsWith('* ') || paragraph.startsWith('- ')) {
                      return (
                        <li key={pIndex} className="text-slate-700 ml-6">
                          {paragraph.substring(2)}
                        </li>
                      );
                    }
                    
                    if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                      return (
                        <h3 key={pIndex} className="text-xl font-bold text-slate-900 mt-8 mb-4">
                          {paragraph.replace(/\*\*/g, '')}
                        </h3>
                      );
                    }
                    
                    if (paragraph.startsWith('### ')) {
                      return (
                        <h3 key={pIndex} className="text-xl font-bold text-slate-900 mt-8 mb-4">
                          {paragraph.replace('### ', '')}
                        </h3>
                      );
                    }
                    
                    return (
                      <p key={pIndex} className="text-slate-700 leading-relaxed">
                        {paragraph}
                      </p>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Error State */}
        {!loading && !content && (
          <div className="text-center py-12 bg-slate-50 rounded-lg">
            <i className="ri-error-warning-line text-6xl text-slate-300 mb-4"></i>
            <p className="text-xl text-slate-600">Unable to load transcript</p>
            <p className="text-slate-500 mt-2">Please try again later</p>
          </div>
        )}

        {/* Related Resources */}
        <section className="mt-16 pt-12 border-t border-slate-200">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Related Resources</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/constitutional-ai"
              className="bg-slate-50 rounded-lg p-6 hover:shadow-lg transition-all duration-300 cursor-pointer group"
            >
              <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-red-800 transition-colors duration-300">
                Constitutional AI Framework
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                Learn about the technical architecture and implementation of Constitutional AI
              </p>
              <div className="flex items-center gap-2 text-red-800 font-semibold text-sm">
                Read More
                <i className="ri-arrow-right-line"></i>
              </div>
            </a>

            <a
              href="/ai-liability"
              className="bg-slate-50 rounded-lg p-6 hover:shadow-lg transition-all duration-300 cursor-pointer group"
            >
              <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-red-800 transition-colors duration-300">
                AI Liability Framework
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                Explore the legal foundations for AI accountability and design defect liability
              </p>
              <div className="flex items-center gap-2 text-red-800 font-semibold text-sm">
                Read More
                <i className="ri-arrow-right-line"></i>
              </div>
            </a>
          </div>
        </section>

        {/* Download Section */}
        <section className="mt-12 mb-12">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleDownload}
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-red-800 hover:bg-red-900 text-white font-semibold transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-download-2-line text-xl"></i>
              Download Transcript
            </button>
            <a
              href="https://soundcloud.com/albertorocha329/existing_law_forces_ai_transpa"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-slate-900 hover:bg-slate-800 text-white font-semibold transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-music-2-line text-xl"></i>
              Listen on SoundCloud
            </a>
          </div>
          <p className="text-sm text-slate-500 mt-3 text-center">
            Free download • No registration required
          </p>
        </section>
      </div>

      <Footer />
    </div>
  );
}
